import React, { useState, useEffect, useRef } from 'react';

const COLOR_GRAY = '#667781';
const COLOR_BLUE = '#53bdeb';
const COLOR_RED = '#e74c3c';

const ClockIcon = () => (
	<svg width="11" height="11" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<circle cx="8" cy="8" r="7" stroke={COLOR_GRAY} strokeWidth="1.5" />
		<path d="M8 4.5V8.5L10.5 10" stroke={COLOR_GRAY} strokeWidth="1.5" strokeLinecap="round" />
	</svg>
);

const SingleCheckIcon = () => (
	<svg width="13" height="11" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M1.5 5.5L5 9L11.5 2" stroke={COLOR_GRAY} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const DoubleCheckIcon = ({ color }) => (
	<svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M1 5.5L4.5 9L11 2" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M5 5.5L8.5 9L15 2" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const ErrorIcon = () => (
	<svg width="13" height="13" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<circle cx="8" cy="8" r="7" fill={COLOR_RED} />
		<path d="M8 4V9" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
		<circle cx="8" cy="11.5" r="0.9" fill="white" />
	</svg>
);

const ERROR_REASON_MAP = {
	'Recipient opted out'                             : 'Contato optou por não receber mensagens',
	'Campanha pausada'                                : 'Campanha pausada',
	'Limite de envios diário excedido para esse telefone' : 'Limite diário de envios atingido para este número',
	'Failed to publish event to queue'               : 'Falha interna ao enfileirar mensagem',
};

function friendlyError( errorMessage ) {
	if ( !errorMessage ) return 'Erro ao enviar mensagem';
	for ( const [ key, label ] of Object.entries( ERROR_REASON_MAP ) ) {
		if ( errorMessage.includes( key ) ) return label;
	}
	// Mensagens da Meta API (ex: "Meta API Error: 131047 - ...")
	const metaMatch = errorMessage.match( /Meta API Error:\s*(\d+)\s*-\s*(.+)/ );
	if ( metaMatch ) {
		const code    = Number( metaMatch[1] );
		const metaMsg = metaMatch[2]?.replace( /^\(#\d+\)\s*/, '' ).trim(); // remove "(#132001) " do início
		if ( code === 131047 ) return 'Fora da janela de 24 horas';
		if ( code === 131026 ) return 'Número não tem WhatsApp';
		if ( code === 131048 ) return 'Limite de spam atingido — tente novamente mais tarde';
		if ( code === 368 )    return 'Conta temporariamente bloqueada pela Meta';
		if ( code === 470 )    return 'Mensagem rejeitada — template inválido ou expirado';
		if ( code === 132001 ) return 'Template não encontrado na Meta — verifique o nome do template';
		if ( code === 132000 ) return 'Template não aprovado ou com status inativo na Meta';
		if ( code === 132007 ) return 'Parâmetros do template inválidos';
		// Para outros códigos, mostra a descrição real que a Meta retornou
		return metaMsg ? `Erro Meta ${code}: ${metaMsg}` : `Erro Meta ${code}`;
	}
	return errorMessage.length > 80 ? errorMessage.slice( 0, 77 ) + '...' : errorMessage;
}

const ErrorTooltip = ( { errorMessage } ) => {
	const [ open, setOpen ] = useState( false );
	const [ copied, setCopied ] = useState( false );
	const ref = useRef( null );

	useEffect( () => {
		if ( !open ) return;
		const handleClickOutside = ( e ) => {
			if ( ref.current && !ref.current.contains( e.target ) ) {
				setOpen( false );
			}
		};
		document.addEventListener( 'mousedown', handleClickOutside );
		return () => document.removeEventListener( 'mousedown', handleClickOutside );
	}, [ open ] );

	const label = friendlyError( errorMessage );

	const handleCopy = ( e ) => {
		e.stopPropagation();
		navigator.clipboard.writeText( errorMessage || label ).then( () => {
			setCopied( true );
			setTimeout( () => setCopied( false ), 1500 );
		} );
	};

	return (
		<span
			ref={ref}
			style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
			onClick={( e ) => { e.stopPropagation(); setOpen( prev => !prev ); }}
		>
			<ErrorIcon />
			{open && (
				<span
					onClick={handleCopy}
					title="Clique para copiar"
					style={{
						position        : 'absolute',
						bottom          : '20px',
						right           : '0',
						backgroundColor : copied ? '#1a7f4b' : '#333',
						color           : '#fff',
						fontSize        : '12px',
						lineHeight      : '1.4',
						padding         : '6px 10px',
						borderRadius    : '6px',
						whiteSpace      : 'nowrap',
						boxShadow       : '0 2px 8px rgba(0,0,0,0.25)',
						zIndex          : 100,
						cursor          : 'copy',
						transition      : 'background-color 0.2s',
					}}>
					{copied ? 'Copiado!' : label}
					<span style={{
						position    : 'absolute',
						bottom      : '-5px',
						right       : '4px',
						width       : '0',
						height      : '0',
						borderLeft  : '5px solid transparent',
						borderRight : '5px solid transparent',
						borderTop   : `5px solid ${copied ? '#1a7f4b' : '#333'}`,
						transition  : 'border-top-color 0.2s',
					}} />
				</span>
			)}
		</span>
	);
};

const MessageStatus = ( { status, errorMessage } ) => {
	if ( status === 0 || status === 5 || status === 99 ) {
		return <ErrorTooltip errorMessage={errorMessage} />;
	}

	let icon = null;
	let label = '';
	if ( status === 'pending' || status === 1 ) { icon = <ClockIcon />;                           label = 'Aguardando envio'; }
	else if ( status === 2 )                    { icon = <SingleCheckIcon />;                     label = 'Enviado'; }
	else if ( status === 3 )                    { icon = <DoubleCheckIcon color={COLOR_GRAY} />;  label = 'Entregue'; }
	else if ( status === 4 )                    { icon = <DoubleCheckIcon color={COLOR_BLUE} />;  label = 'Lido'; }
	if ( !icon ) return null;
	return <span title={label} style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>;
};

export { MessageStatus };
